/**
 * Team settings section (pirx-z0v4g.7).
 *
 * Shows the members of the active team and — for owners/admins — a form to
 * invite someone. Scoped to the team selected in the nav switcher (pirx-z0v4g.6)
 * via useActiveTeam. Renders nothing for a user with no teams. Owner/admin-only
 * actions are hidden for plain members; the backend re-checks every action, so
 * hiding is UX, not the security boundary.
 *
 * Invites live in a separate store from the member roster and there is no email
 * transport wired, so a created invite surfaces its accept link here for the
 * inviter to share manually — that is how the flow completes end to end.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/useAuth";
import { useActiveTeam } from "@/services/teams/useActiveTeam";
import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type InvitableRole,
  type TeamDetails,
  createTeamInvite,
  getTeam,
} from "@/services/api-client";
import { ApiError } from "@/services/api/errors";

function inviteAcceptUrl(token: string): string {
  return `${window.location.origin}/teams/accept?token=${encodeURIComponent(token)}`;
}

export default function SettingsTeamsSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { profile } = useAuth();
  const { teams, activeTeamId, activeTeam } = useActiveTeam(profile);

  const [details, setDetails] = useState<TeamDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InvitableRole>("member");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  // Sequence guard: a fast A -> B -> A switch can leave the slow first
  // getTeam(A) resolving after getTeam(B) and clobbering the fresher
  // roster/role under `canManage` (same pattern as useAuditHistory).
  const requestSeqRef = useRef(0);

  const loadDetails = useCallback(async (teamId: string) => {
    const requestId = ++requestSeqRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const teamDetails = await getTeam(teamId);
      if (requestId !== requestSeqRef.current) return;
      setDetails(teamDetails);
    } catch {
      if (requestId !== requestSeqRef.current) return;
      setDetails(null);
      setLoadError(t("settings:teams.loadError"));
    } finally {
      if (requestId === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    if (!activeTeamId) {
      setDetails(null);
      return;
    }
    void loadDetails(activeTeamId);
  }, [activeTeamId, loadDetails]);

  if (teams.length === 0) {
    return null;
  }

  const canManage = details?.currentUserRole === "owner" ||
    details?.currentUserRole === "admin";

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTeamId || !email.trim()) return;
    setInviting(true);
    setInviteError(null);
    setInviteLink(null);
    setInvitedEmail(null);
    try {
      const result = await createTeamInvite(activeTeamId, {
        email: email.trim(),
        role,
      });
      setInvitedEmail(result.email);
      setInviteLink(inviteAcceptUrl(result.token));
      setEmail("");
    } catch (err) {
      setInviteError(
        err instanceof ApiError && err.message
          ? err.message
          : t("settings:teams.inviteError"),
      );
    } finally {
      setInviting(false);
    }
  };

  return (
    <section
      className="space-y-6 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6"
      data-test="settings-teams-section"
    >
      <div>
        <h2 className="text-lg font-semibold text-[var(--foreground)]">
          {t("settings:teams.title")}
        </h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          {t("settings:teams.subtitle")}
        </p>
      </div>

      {!activeTeamId ? (
        <p
          className="text-sm text-[var(--muted-foreground)]"
          data-test="settings-teams-select-prompt"
        >
          {t("settings:teams.selectPrompt")}
        </p>
      ) : loading ? (
        <div
          aria-label={t("common:labels.loading")}
          className="animate-pulse space-y-5"
          data-test="settings-teams-loading"
          role="status"
        >
          <div className="h-5 w-40 rounded-[var(--r-sm)] bg-[var(--secondary)]" />
          <div className="grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[72px] rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--secondary)]"
              />
            ))}
          </div>
          <div className="h-40 w-full rounded-[var(--r-md)] border border-[var(--border)] bg-[var(--secondary)]" />
        </div>
      ) : loadError ? (
        <Alert data-test="settings-teams-error" variant="error">
          <AlertDescription className="flex-1">{loadError}</AlertDescription>
          <AlertAction>
            <Button
              data-test="settings-teams-retry"
              onClick={() => activeTeamId && void loadDetails(activeTeamId)}
              size="sm"
              variant="outline"
            >
              {t("common:buttons.retry")}
            </Button>
          </AlertAction>
        </Alert>
      ) : details ? (
        <>
          <div className="space-y-5">
            <h3 className="font-heading text-sm font-medium text-[var(--foreground)]">
              {activeTeam?.name ?? details.name}
            </h3>

            {/* Stat cards */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-[var(--border)] bg-[var(--secondary)] px-4 py-3">
                <span className="block font-heading text-xl font-semibold text-[var(--foreground)]">
                  {details.members.length}
                </span>
                <span className="text-xs text-[var(--muted-foreground)]">
                  {t("settings:teams.statMembers")}
                </span>
              </div>
              <div className="rounded-lg border border-[var(--border)] bg-[var(--secondary)] px-4 py-3">
                <span className="block font-heading text-xl font-semibold text-[var(--foreground)]">
                  {details.members.filter((m) => m.acceptedAt === null).length}
                </span>
                <span className="text-xs text-[var(--muted-foreground)]">
                  {t("settings:teams.statPending")}
                </span>
              </div>
              <div className="rounded-lg border border-[var(--border)] bg-[var(--secondary)] px-4 py-3">
                <span className="block font-heading text-xl font-semibold text-[var(--foreground)]">
                  {t(`settings:teams.roles.${details.currentUserRole}`)}
                </span>
                <span className="text-xs text-[var(--muted-foreground)]">
                  {t("settings:teams.statRole")}
                </span>
              </div>
            </div>

            {/* Member roster */}
            <Table data-test="settings-teams-members">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("settings:teams.colMember")}</TableHead>
                  <TableHead>{t("settings:teams.colRole")}</TableHead>
                  <TableHead>{t("settings:teams.colStatus")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {details.members.map((m) => (
                  <TableRow key={m.id} data-test="settings-teams-member">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          name={m.name || m.email}
                          size={30}
                          tone="brand"
                        />
                        <div className="min-w-0">
                          <span className="block truncate text-sm text-[var(--foreground)]">
                            {m.name || m.email}
                          </span>
                          <span className="block truncate text-xs text-[var(--muted-foreground)]">
                            {m.email}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-[var(--muted-foreground)]">
                        {t(`settings:teams.roles.${m.role}`)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {m.acceptedAt === null
                        ? (
                          <Badge
                            variant="warning"
                            size="sm"
                            data-test="settings-teams-member-pending"
                          >
                            {t("settings:teams.pending")}
                          </Badge>
                        )
                        : (
                          <Badge variant="success" size="sm">
                            {t("settings:teams.active")}
                          </Badge>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Roles legend (3-role model) */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--muted-foreground)]">
              <span className="font-medium">
                {t("settings:teams.rolesLegendTitle")}:
              </span>
              {(["owner", "admin", "member"] as const).map((r) => (
                <Badge key={r} variant="outline" size="sm">
                  {t(`settings:teams.roles.${r}`)}
                </Badge>
              ))}
            </div>
          </div>

          {canManage && (
            <form
              className="space-y-3 border-t border-[var(--border)] pt-4"
              onSubmit={handleInvite}
              data-test="settings-teams-invite-form"
            >
              <h3 className="text-sm font-medium text-[var(--foreground)]">
                {t("settings:teams.inviteTitle")}
              </h3>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto] sm:items-end">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-email">
                    {t("settings:teams.inviteEmailLabel")}
                  </Label>
                  <Input
                    id="invite-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("settings:teams.inviteEmailPlaceholder")}
                    data-test="settings-teams-invite-email"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-role">
                    {t("settings:teams.inviteRoleLabel")}
                  </Label>
                  <select
                    id="invite-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as InvitableRole)}
                    data-test="settings-teams-invite-role"
                    className="h-8.5 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none sm:h-7.5"
                  >
                    <option value="member">{t("settings:teams.roles.member")}</option>
                    <option value="admin">{t("settings:teams.roles.admin")}</option>
                  </select>
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={inviting}
                  data-test="settings-teams-invite-submit"
                >
                  {inviting
                    ? t("settings:teams.inviteSending")
                    : t("settings:teams.inviteSubmit")}
                </Button>
              </div>
              {inviteError && (
                <p className="text-sm text-[var(--destructive)]" data-test="settings-teams-invite-error">
                  {inviteError}
                </p>
              )}
              {inviteLink && invitedEmail && (
                <Alert data-test="settings-teams-invite-success" variant="info">
                  <AlertDescription className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm text-[var(--foreground)]">
                      {t("settings:teams.inviteCreated", { email: invitedEmail })}
                    </p>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      {t("settings:teams.inviteShareHint")}
                    </p>
                    <code
                      className="block break-all text-xs text-[var(--foreground)]"
                      data-test="settings-teams-invite-link"
                    >
                      {inviteLink}
                    </code>
                  </AlertDescription>
                </Alert>
              )}
            </form>
          )}
        </>
      ) : null}
    </section>
  );
}
